<template>
  <div class="rate-shop flex flex-column flex-gap-1">
    <MessageContainer :messages="messages" message-class="rygen-message" />

    <RateShopBenchmarkRow
      v-model:freight-allowance="freightAllowanceInternal"
      :benchmarks="benchmarks"
      :rate-shop="rateShop"
      :show-freight-allowance="shouldShowFreightAllowance"
      :freight-allowance-editable="isFreightAllowanceEditable"
    />

    <RateShopServiceTypeFilters
      v-model="serviceTypeFilters"
      :rate-shop="rateShop"
    />

    <RoutingGuide
      v-if="routingGuideRule"
      :id="`${id}-routing-guide`"
      :rate-shop="rateShop"
      :editable="editable"
      :rateable="selectable"
      :service-type-filters="serviceTypeFilters"
      :service-type-filter-set="isAnyServiceTypeFilterSet"
      @edit-quote="emit('edit-quote', $event)"
      @edit-spot-quote="emit('edit-quote', $event)"
      @rate-selected="onRateSelected"
      @send-to-brokerage="onBrokerageRateSelected"
    />
    <RateShopTable
      v-else
      :id="`${id}-main-rates`"
      :rate-quotes="filteredRateQuotes"
      :view-uplift="viewUplift"
      :editable="editable"
      :selectable="selectable"
      :loading="isPolling"
      @edit-quote="emit('edit-quote', $event)"
      @edit-spot-quote="emit('edit-quote', $event)"
      @rate-selected="onRateSelected"
      @send-to-brokerage="onBrokerageRateSelected"
    >
      <template #empty>
        {{ t('common.noParam', { param: t('rateShop.rates', 2) }) }}
      </template>
    </RateShopTable>

    <template v-if="showRateErrors">
      <h3>{{ t('rateShop.unrated') }}</h3>
      <RateShopTable
        :id="`${id}-rate-errors`"
        :rate-quotes="rateErrors"
        :loading="isPolling"
        price-class="text-danger font-medium"
      />
    </template>

    <RateSelectionReasonModal
      v-model:visible="lowestCostReasonModal.visible"
      :lowest-price-quote="lowestPriceQuote"
      :selected-quote="lowestCostReasonModal.selectedQuote"
      @confirm="onRateSelectionConfirmed"
    />
  </div>
</template>

<script lang="ts" setup>
import { usePrimeVue } from 'primevue/config';
import type { PropType } from 'vue';
import { computed, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import authorization from '@rygen/rygen-ui/authorization';
import { useUid } from '@rygen/rygen-ui/composables';
import {
  AccessorialCodeWellKnownNaturalId,
  Loadable,
} from '@rygen/rygen-ui/model';

import {
  useFreightAllowance,
  useLowestCostReasonCode,
  useRateShop,
  useRoutingGuide,
} from '@/composables';
import {
  type AcceptQuotePayload,
  FreightAllowance,
  type MessageSpecification,
  RateQuote,
  RateSelectionReason,
  RateShop,
  RateShopStatus,
  type ServiceTypeFilters,
} from '@/model';
import { useCreditUsageStore } from '@/stores/CreditUsage';
import { useParentEntityStore } from '@/stores/ParentEntity';

import RateSelectionReasonModal from '@/components/rateShop/RateSelectionReasonModal.vue';
import RateShopBenchmarkRow from '@/components/rateShop/RateShopBenchmarkRow.vue';
import RateShopServiceTypeFilters from '@/components/rateShop/RateShopServiceTypeFilters.vue';
import RateShopTable from '@/components/rateShop/RateShopTable.vue';
import RoutingGuide from '@/components/rateShop/RoutingGuide.vue';
import MessageContainer from '@/components/ui/MessageContainer.vue';

const serviceTypeFilters = defineModel('serviceTypeFilters', {
  type: Object as PropType<ServiceTypeFilters>,
  default: () => ({}),
});

const props = defineProps({
  id: {
    type: String,
    default: () => useUid('rate-shop').uid,
  },
  rateShop: {
    type: Object as PropType<Loadable<RateShop>>,
    required: true,
  },
  /**
   * The Freight Allowance Amount for the entity (e.g. Shipment, ShipmentSpec) this rate shop represents
   */
  freightAllowance: {
    type: Object as PropType<FreightAllowance | undefined>,
    default: () => undefined,
  },
  selectable: {
    type: Boolean,
    default: () => false,
  },
  updatable: {
    type: Boolean,
    default: () => false,
  },
  viewUplift: {
    type: Boolean,
    default: () => false,
  },
  editable: {
    type: Boolean,
    default: () => false,
  },
  lineItemsMissingDims: {
    type: Boolean,
    default: () => false,
  },
});
const {
  id,
  rateShop,
  freightAllowance,
  selectable,
  updatable,
  editable,
  lineItemsMissingDims,
} = toRefs(props);
const emit = defineEmits<{
  (event: 'edit-quote', rateQuote: RateQuote): void;
  (event: 'rate-selected', payload: AcceptQuotePayload): void;
  (event: 'rate-shop-completed'): void;
  (event: 'send-to-brokerage', payload: Partial<AcceptQuotePayload>): void;
  (
    event: 'update:freight-allowance',
    freightAllowance?: FreightAllowance,
  ): void;
}>();

const { creditUsage } = useCreditUsageStore();
const {
  freightAllowanceInternal,
  isFreightAllowanceEditable,
  shouldShowFreightAllowance,
} = useFreightAllowance(freightAllowance, emit);
const { t } = useI18n();
const { isRateSelectionReasonRequired } = useLowestCostReasonCode();
const { parentEntity } = useParentEntityStore();
const {
  benchmarks,
  lowestPriceQuote,
  filteredRateQuotes,
  isAnyServiceTypeFilterSet,
  rateErrors,
  showRateErrors,
  requiresReRate,
} = useRateShop(rateShop, { serviceTypeFilters });
const { routingGuideRule } = useRoutingGuide(rateShop, serviceTypeFilters);

const lowestCostReasonModal = ref({
  visible: false,
  selectedQuote: new RateQuote(),
  isBrokerage: false,
});

const shouldPromptForRateSelectionReason = computed(() => {
  return (
    isRateSelectionReasonRequired.value &&
    lowestPriceQuote.value &&
    lowestCostReasonModal.value.selectedQuote &&
    lowestCostReasonModal.value.selectedQuote.totalPrice &&
    lowestCostReasonModal.value.selectedQuote.totalPrice >
      (lowestPriceQuote.value.totalPrice ?? 0)
  );
});

const isPolling = computed(() => {
  return (
    rateShop.value.loading ||
    (rateShop.value.data?.status !== RateShopStatus.COMPLETED &&
      rateShop.value.data?.status !== RateShopStatus.ERROR &&
      !rateShop.value.error)
  );
});

const hasMiscAccessorial = computed(() => {
  const requestedAccessorials = parentEntity.value?.requestedAccessorials ?? [];
  return requestedAccessorials.some(
    (a) => a.naturalId === AccessorialCodeWellKnownNaturalId.MISC,
  );
});

const messages = computed((): MessageSpecification[] => [
  {
    id: 'select-warning',
    text: t('rateShop.selectInstructions'),
    severity: 'warn',
    visible:
      authorization.hasAuthority('rateShop', 'selectRate') &&
      !isPolling.value &&
      updatable.value &&
      !selectable.value,
  },
  {
    id: 'error-fetching-rates-warning',
    text: t('common.concatParams', {
      param1: t('rateShop.errorFetchingRates'),
      param2: updatable.value ? t('rateShop.retryFetch') : '',
    }),
    severity: 'error',
    visible:
      (rateShop.value.data?.status === RateShopStatus.ERROR ||
        !!rateShop.value.error) &&
      !isPolling,
  },
  {
    id: 'invalid-rates-warning',
    text: t('common.concatParams', {
      param1: t('rateShop.invalidRates'),
      param2: updatable.value ? t('rateShop.requiresReRate') : '',
    }),
    severity: 'warn',
    visible: requiresReRate.value && !isPolling.value,
  },
  {
    id: 'approaching-credit-limit-warning',
    text: t('creditLimits.approachingLimitMessage'),
    severity: 'warn',
    icon: 'pi pi-exclamation-triangle',
    visible:
      !creditUsage.value.loading &&
      creditUsage.value.data?.standing === 'APPROACHING_LIMIT',
  },
  {
    id: 'exceeded-credit-limit-warning',
    text: t('creditLimits.exceededLimitMessage'),
    severity: 'error',
    icon: 'pi pi-exclamation-triangle',
    visible:
      !creditUsage.value.loading &&
      creditUsage.value.data?.limitExceeded === true,
  },
  {
    id: 'missing-ltl-info-warning',
    text: t('lineItems.missingLtlInfoMessage'),
    severity: 'info',
    visible: lineItemsMissingDims.value && !isPolling.value,
  },
  {
    id: 'misc-accessorial',
    text: t('referenceData.miscAccessorialInfo'),
    severity: 'info',
    visible: hasMiscAccessorial.value,
  },
]);

const onRateSelected = (rateQuote: RateQuote) => {
  lowestCostReasonModal.value.selectedQuote = rateQuote;
  if (shouldPromptForRateSelectionReason.value) {
    lowestCostReasonModal.value.visible = true;
  } else {
    emit('rate-selected', {
      rateQuote,
      rateSelectionReason: undefined,
    });
  }
};

const onBrokerageRateSelected = (rateQuote: RateQuote) => {
  lowestCostReasonModal.value.selectedQuote = rateQuote;
  if (shouldPromptForRateSelectionReason.value) {
    lowestCostReasonModal.value.isBrokerage = true;
    lowestCostReasonModal.value.visible = true;
  } else {
    emit('send-to-brokerage', {
      rateQuote,
      rateSelectionReason: undefined,
    });
  }
};

const onRateSelectionConfirmed = (payload: RateSelectionReason) => {
  if (!payload.rateSelectionReasonType) {
    return;
  }

  const rateSelectionReason = new RateSelectionReason(payload);
  const acceptQuotePayload = {
    rateQuote: lowestCostReasonModal.value.selectedQuote,
    rateSelectionReason,
  };

  if (lowestCostReasonModal.value.isBrokerage) {
    emit('send-to-brokerage', acceptQuotePayload);
  } else {
    emit('rate-selected', acceptQuotePayload);
  }
};

watch(
  () => rateShop.value.data?.status,
  (newVal?: RateShopStatus, oldVal?: RateShopStatus) => {
    //only emit rate-shop-completed if we are transitioning to completed state
    //i.e. a new rateshop is done polling
    if (
      oldVal !== undefined &&
      oldVal !== RateShopStatus.COMPLETED &&
      newVal === RateShopStatus.COMPLETED
    ) {
      emit('rate-shop-completed');
    }
  },
);

defineExpose({
  $primevue: usePrimeVue(),
});
</script>

<style>
.rate-shop {
  .p-message {
    margin-block: 0;
  }

  .filter-button {
    white-space: nowrap;
  }
}
</style>
